import { Download, Share, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { InstallPromptState } from '@/pwa/use-install-prompt'

interface InstallPromptProps {
  state: InstallPromptState
}

/**
 * Small, non-blocking install notification.
 *
 * It sits in a corner (never over the POS sale button or the checkout CTA),
 * is fully keyboard operable and is rendered only on routes where it cannot
 * cover a transaction (see `isInterruptiblePath`).
 */
export function InstallPrompt({ state }: InstallPromptProps) {
  if (!state.visible) return null

  const shell =
    'fixed z-[60] right-3 bottom-3 sm:right-4 sm:bottom-4 w-[min(22rem,calc(100vw-1.5rem))] ' +
    'rounded-xl border border-slate-200 bg-white p-4 shadow-lg shadow-slate-900/10 ' +
    'pb-[calc(1rem+env(safe-area-inset-bottom))]'

  const close = (
    <button
      type="button"
      onClick={state.dismiss}
      aria-label="Dismiss install prompt"
      className="absolute top-2 right-2 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
    >
      <X className="h-4 w-4" aria-hidden="true" />
    </button>
  )

  if (state.capability === 'ios-guide') {
    return (
      <aside className={`${shell} relative`} role="region" aria-label="Install StockWise">
        {close}
        <div className="flex items-start gap-3 pr-6">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <Share className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Install StockWise</h2>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">
              Tap the <strong>Share</strong> button in Safari, then choose{' '}
              <strong>Add to Home Screen</strong> to keep StockWise one tap away.
            </p>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button size="sm" variant="outline" onClick={state.dismiss}>
            Got it
          </Button>
        </div>
      </aside>
    )
  }

  if (state.capability === 'browser-menu') {
    return (
      <aside className={`${shell} relative`} role="region" aria-label="Install StockWise">
        {close}
        <div className="flex items-start gap-3 pr-6">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <Download className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Install StockWise</h2>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">
              Open your browser menu and choose <strong>Install StockWise</strong> (or{' '}
              <strong>Install app</strong>) to keep it in your dock and launch it full-screen.
            </p>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button size="sm" variant="outline" onClick={state.dismiss}>
            Got it
          </Button>
        </div>
      </aside>
    )
  }

  return (
    <aside className={`${shell} relative`} role="region" aria-label="Install StockWise">
      {close}
      <div className="flex items-start gap-3 pr-6">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
          <Download className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Install StockWise</h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">
            Get quicker access to your shop management system. Install StockWise on your device for
            an app-like experience.
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={state.defer}>
          Maybe later
        </Button>
        <Button size="sm" onClick={() => void state.install()}>
          Install StockWise
        </Button>
      </div>
    </aside>
  )
}

export default InstallPrompt
